import * as CL from '@design-system/component-library';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { useNavigateBack } from '../../common/hooks/useNavigateBack';
import { useTitle } from '../../common/hooks/useTitle';
import {
  addMsg,
  additionalServicesMsg,
  cartIsEmptyMsg,
  checkoutMsg,
  continueShoppingMsg,
  paymentPeriodMsg,
  quantityMsg,
  removeMsg,
  shoppingCartContentPluralMsg,
  shoppingCartContentSingularMsg,
  shoppingCartMsg,
  subtractMsg,
  t,
  totalMsg,
} from '../../common/i18n/index.js';
import { setFooterVisibility } from '../../selfservice/actions';
import { getUserHasSeenCompanySelectionNotificationShoppingBasket } from '../../selfservice/common/localStorageUtils';
import { SquareTradeErrorModal } from '../SquareTradeErrorModal/SquareTradeErrorModal';
import { LoginBanner } from './LoginBanner.js';
import { ShoppingBasketCompanySelector } from './ShoppingBasketCompanySelector';
import { shoppingBasketItemsCoCo } from './ShoppingBasketItemsCoCo.js';
import { formatTotalPrices } from './shoppingBasketHelpersCoCo';
import type { HeaderUserAccount } from '../../common/types/authentication';
import type { ProductOfferingCartItems, ShoppingBasketType } from '../../common/types/shoppingBasketCoCo';

import './ShoppingBasket.scss';

export interface ShoppingBasketProps {
  isEmployee?: boolean;
  shoppingBasket: ShoppingBasketType;
  loggedIn: boolean;
  updateCartItemQuantity: (newQty: number, groupItems: ProductOfferingCartItems[]) => void;
  updateCartItemAddonSelection: (addonId: string, groupCartItemIds: string[]) => void;
  updateCartItemAddonGroupSelection?: (
    groupCartItemIds: string[],
    groupId: string,
    selectedOfferingIds: string[]
  ) => void;
  updateCartItemRuntimeCharacteristic?: (groupCartItemIds: string[], selectedValue: string) => void;
  userAccounts: HeaderUserAccount[];
  loginInProgress: boolean;
  updateBasket: (basketJson: string) => void;
}

export const ShoppingBasketCoCo = ({
  isEmployee = false,
  shoppingBasket,
  loggedIn,
  updateCartItemQuantity,
  userAccounts,
  loginInProgress,
  updateBasket: _updateBasket,
  updateCartItemAddonSelection,
  updateCartItemAddonGroupSelection,
  updateCartItemRuntimeCharacteristic,
}: ShoppingBasketProps) => {
  useTitle([t.BE8Q(shoppingCartMsg)]);
  const location = useLocation();
  const navigate = useNavigate();
  const navigateBack = useNavigateBack(paths.WEB_SHOP, -1);
  const [showSquareTradeModal, setShowSquareTradeModal] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setFooterVisibility(true));
  }, [dispatch]);

  const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (isEmployee) {
      const selectedList = shoppingBasket.productOfferings.find(item => item.employeeProductList)?.employeeProductList;
      navigate(
        selectedList ? paths.EMPLOYEE_DEVICE_STORE_PHONES : paths.EMPLOYEE_DEVICE_STORE,
        selectedList ? { state: { employeeProductList: selectedList } } : undefined
      );
      return;
    }
    // If user came from checkout, navigate back to web shop main page.
    // Otherwise user could be navigated back to checkout in some cases.
    location.state?.from === 'kassa' ? navigate(paths.WEB_SHOP) : navigateBack();
  };

  const productOfferings = shoppingBasket?.productOfferings || [];

  const totalProductQuantity = productOfferings.length;

  const showCompanySelector = !isEmployee && loggedIn && userAccounts.length > 1;
  const items = shoppingBasketItemsCoCo({
    basketItems: productOfferings,
    onQuantityChange: updateCartItemQuantity,
    onAddOnChange: updateCartItemAddonSelection,
    onAddOnGroupSelectionChange: updateCartItemAddonGroupSelection,
    onRuntimeCharacteristicChange: updateCartItemRuntimeCharacteristic,
  });
  const totals = formatTotalPrices(productOfferings);

  const paddingForPopover =
    showCompanySelector &&
    !loginInProgress &&
    !getUserHasSeenCompanySelectionNotificationShoppingBasket() &&
    window.innerWidth < 481
      ? 'of-shopping-basket-popover-padding'
      : undefined;

  const handleCheckout = (e?: React.MouseEvent<HTMLElement>) => {
    e?.preventDefault();
    if (isEmployee) {
      navigate(paths.EMPLOYEE_DEVICE_CHECKOUT);
      return;
    }
    if (!loggedIn) {
      navigate(paths.SHOPPING_BASKET, { state: { loginInProgress: true } });
    } else {
      navigate(paths.PLACE_ORDER);
    }
  };

  return (
    <>
      <SquareTradeErrorModal isOpen={showSquareTradeModal} onClose={() => setShowSquareTradeModal(false)} />
      <div className={`of-shopping-basket-wrapper${isEmployee ? ' of-shopping-basket-wrapper--employee' : ''}`}>
        <div className={`${!loginInProgress ? dsClass.BACKGROUND_COLOR_NEUTRAL_200 : ''}`}>
          <div className={`of-heading-and-company-selector ${dsClass.PADDING_BOTTOM_4}`}>
            <h1
              className={`${dsClass.H1} ${dsClass.MARGIN_BOTTOM_4}  ${dsClass.MARGIN_TOP_0} ${paddingForPopover} of-shopping-basket-header`}
            >
              {t.BE8Q(shoppingCartMsg)}
            </h1>
            {showCompanySelector && (
              <ShoppingBasketCompanySelector userAccounts={userAccounts} showInDialog={loginInProgress} />
            )}
          </div>
        </div>
        <div className="of-shopping-basket-content">
          {!isEmployee && !loggedIn && (
            <div className="of-with-horizontal-padding">
              <LoginBanner />
            </div>
          )}

          <CL.ShoppingCart
            ariaAddonsLabel={t.LXSR(additionalServicesMsg)}
            ariaPaymentLabel={t.EM2Q(paymentPeriodMsg)}
            ariaQuantityDeleteLabel={t.R3VE(removeMsg)}
            ariaQuantityLabel={t.M0W7(quantityMsg)}
            ariaQuantityMinusLabel={t.C2KQ(subtractMsg)}
            ariaQuantityPlusLabel={t.VKFM(addMsg)}
            ariaTotalsLabel={t.CEQ2(totalMsg)}
            checkoutUrl={isEmployee ? paths.EMPLOYEE_DEVICE_CHECKOUT : paths.PLACE_ORDER}
            i18nCheckoutLabel={t.UAAP(checkoutMsg)}
            i18nContinueLabel={t.VLZR(continueShoppingMsg)}
            i18nEmptyLabel={t.PRFW(cartIsEmptyMsg)}
            onCheckout={handleCheckout}
            onCancel={handleCancel}
            i18nHeading="" // Leave DS Shopping Cart heading empty and use own. This results in two H1 elements, which is not a good thing SEO-wise.
            caption={
              totalProductQuantity === 1
                ? t.I23L(shoppingCartContentSingularMsg)
                : t.PS1E(shoppingCartContentPluralMsg, `${totalProductQuantity}`)
            }
            items={items}
            onPaymentOptionChange={() => {}} // Not used at the moment
            onQuantityChange={() => {}} // Not used at the moment
            totals={totals}
            totalProductQuantity={totalProductQuantity}
          />
        </div>
      </div>
    </>
  );
};
